<template><div><code-example only-show="demo" class="d-d-block">
<ul>
  <dt-list-item navigation-type="tab">
    <template #start>
      <dt-icon size="300" name="check" />
    </template>
    <span>Default List Item</span>
    <template #subtitle>
      Description
    </template>
    <template #bottom>
      <dt-badge text="Label" />
    </template>
    <template #end>
      <dt-icon size="300" name="external-link" />
    </template>
  </dt-list-item>
</ul>
</code-example>
<!-- <component-combinator component-name="DtListItem" /> -->
<h2 id="base-style" tabindex="-1" class="d-docsite--header-2">Base Style <a class="header-anchor d-link d-docsite--link d-link" href="#base-style" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A list item provides accessibility controls and common functionality. The component uses child components that provide styling and slots for different types of list items. If you want to create a custom list item you can pass a type &quot;custom&quot;, which will let you define the structure of the content.</p>
<p class="d-docsite--paragraph">The default list item has 5 slots that can be used for the most common use cases, <strong>start</strong>, <strong>end</strong>, <strong>default</strong>, <strong>subtitle</strong> and <strong>bottom</strong> slot. All of the slots are optional.</p>
<p class="d-docsite--paragraph">The <strong>start</strong> slot can contain content, such as an avatar, that will be positioned at the start (left in LTR) of the main content.</p>
<p class="d-docsite--paragraph">The <strong>end</strong> slot works the same way, but its contents are placed at the end (right in LTR) of the main slot.</p>
<p class="d-docsite--paragraph">The <strong>default</strong> slot contains the main content of the list item.</p>
<p class="d-docsite--paragraph">The <strong>subtitle</strong> slot can be used to display content below the default slot. The slot has smaller text size and lighter color than default slot.</p>
<p class="d-docsite--paragraph">The <strong>bottom</strong> slot can be used to display content below the subtitle slot.</p>
<code-example class="d-d-block" source-code='&lt;ul&gt;
  &lt;dt-list-item navigation-type=&quot;tab&quot;&gt;
    &lt;template #start&gt;
      &lt;dt-icon size=&quot;300&quot; name=&quot;check&quot; /&gt;
    &lt;/template&gt;
    &lt;span&gt;Default List Item&lt;/span&gt;
    &lt;template #subtitle&gt;
      Description
    &lt;/template&gt;
    &lt;template #bottom&gt;
      &lt;dt-badge text=&quot;Label&quot; /&gt;
    &lt;/template&gt;
    &lt;template #end&gt;
      &lt;dt-icon size=&quot;300&quot; name=&quot;external-link&quot; /&gt;
    &lt;/template&gt;
  &lt;/dt-list-item&gt;
&lt;/ul&gt;'>
<ul>
  <dt-list-item navigation-type="tab">
    <template #start>
      <dt-icon size="300" name="check" />
    </template>
    <span>Default List Item</span>
    <template #subtitle>
      Description
    </template>
    <template #bottom>
      <dt-badge text="Label" />
    </template>
    <template #end>
      <dt-icon size="300" name="external-link" />
    </template>
  </dt-list-item>
</ul>
</code-example>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="custom-list-item" tabindex="-1" class="d-docsite--header-3">Custom List Item <a class="header-anchor d-link d-docsite--link d-link" href="#custom-list-item" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When <code v-pre>type</code> is set to &quot;custom&quot; the list item will not render any styles or slots. This type can be used when the list item has to support content that does not work with the default structure.</p>
<code-example class="d-d-block" source-code='&lt;ul&gt;
  &lt;dt-list-item
    navigation-type=&quot;tab&quot;
    type=&quot;custom&quot;
  &gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;start&quot; justify=&quot;between&quot; gap=&quot;500&quot; class=&quot;d-py-100 d-px-150 d-pie-100&quot;&gt;
      &lt;dt-stack align=&quot;baseline&quot; direction=&quot;row&quot; gap=&quot;400&quot;&gt;
        &lt;dt-text
          kind=&quot;body&quot;
          tone=&quot;muted&quot;
          :size=&quot;100&quot;
          datetime=&quot;10:00&quot;
        &gt;
          10:00
        &lt;/dt-text&gt;
        &lt;dt-text :size=&quot;300&quot; density=&quot;300&quot; kind=&quot;body&quot; tone=&quot;secondary&quot;&gt;
          Custom list item example lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua
        &lt;/dt-text&gt;
      &lt;/dt-stack&gt;
      &lt;dt-stack direction=&quot;row&quot;&gt;
        &lt;dt-button
          :size=&quot;100&quot;
          kind=&quot;muted&quot;
          importance=&quot;clear&quot;
          title=&quot;share&quot;
        &gt;
          &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
            &lt;dt-icon
              name=&quot;share&quot;
              :size=&quot;iconSize&quot;
            /&gt;
          &lt;/template&gt;
        &lt;/dt-button&gt;
        &lt;dt-button
          :size=&quot;100&quot;
          kind=&quot;muted&quot;
          importance=&quot;clear&quot;
          title=&quot;star&quot;
        &gt;
          &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
            &lt;dt-icon
              name=&quot;star&quot;
              :size=&quot;iconSize&quot;
            /&gt;
          &lt;/template&gt;
        &lt;/dt-button&gt;
        &lt;dt-button
          :size=&quot;100&quot;
          kind=&quot;muted&quot;
          importance=&quot;clear&quot;
          title=&quot;more&quot;
        &gt;
          &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
            &lt;dt-icon
              name=&quot;more-vertical&quot;
              :size=&quot;iconSize&quot;
            /&gt;
          &lt;/template&gt;
        &lt;/dt-button&gt;
      &lt;/dt-stack&gt;
    &lt;/dt-stack&gt;
  &lt;/dt-list-item&gt;
&lt;/ul&gt;'>
<ul>
  <dt-list-item
    navigation-type="tab"
    type="custom"
  >
    <dt-stack direction="row" align="start" justify="between" gap="500" class="d-py-100 d-px-150 d-pie-100">
      <dt-stack align="baseline" direction="row" gap="400">
        <dt-text
          kind="body"
          tone="muted"
          :size="100"
          datetime="10:00"
        >
          10:00
        </dt-text>
        <dt-text :size="300" density="300" kind="body" tone="secondary">
          Custom list item example lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua
        </dt-text>
      </dt-stack>
      <dt-stack direction="row">
        <dt-button
          :size="100"
          kind="muted"
          importance="clear"
          title="share"
        >
          <template #startIcon="{ iconSize }">
            <dt-icon
              name="share"
              :size="iconSize"
            />
          </template>
        </dt-button>
        <dt-button
          :size="100"
          kind="muted"
          importance="clear"
          title="star"
        >
          <template #startIcon="{ iconSize }">
            <dt-icon
              name="star"
              :size="iconSize"
            />
          </template>
        </dt-button>
        <dt-button
          :size="100"
          kind="muted"
          importance="clear"
          title="more"
        >
          <template #startIcon="{ iconSize }">
            <dt-icon
              name="more-vertical"
              :size="iconSize"
            />
          </template>
        </dt-button>
      </dt-stack>
    </dt-stack>
  </dt-list-item>
</ul>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="listitem" /></div></template>


